import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchResource } from '@app/src/api/fetchHooks'
import { useUpdateResource } from '@app/src/api/updateHooks'
import type { DrawerViewProps } from '@app/src/components/Drawer/DrawerView'
import DrawerView from '@app/src/components/Drawer/DrawerView'
import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import { useSnackbar } from '@app/src/context/SnackbarContext'
import type { AccessPolicy, EntitlementFeature } from '@app/src/types/entitlements'
import { SolutionType } from '@app/src/types/resourceExplorer'
import type { Provider } from '@app/src/types/organizations'
import { NotificationSeverity } from '@app/src/wf-constants'
import { Autocomplete, Box, Chip, Divider, Stack, TextField, Typography } from '@mui/material'
import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { useQueryClient } from '@tanstack/react-query'
import { useDebounce } from 'use-debounce'
import type { BaseTemplate, OrganizationAvailability } from './availabilityHelpers'
import DefaultAccessPolicy from './Policy/DefaultAccessPolicy'
import type { PolicyEdits } from './Policy/templatePolicy'
import { changedPolicies } from './Policy/templatePolicy'
import { useEntitlementPolicies, useSaveEntitlementPolicies } from './Policy/useEntitlementPolicies'

interface TemplateAvailabilityDrawerViewProps<T extends BaseTemplate> extends Omit<DrawerViewProps, 'title'> {
  template: T
  updateEndpoint: string
  invalidateQueryKey: string
  accessPolicyFeatures?: EntitlementFeature[]
  drawerTitleKey?: string
  successMessageKey?: string
  errorMessageKey?: string
}

interface AvailabilitySaveModel {
  solutionAvailability: string[]
  organizationAvailability: number[]
}

const SOLUTION_TYPE_OPTIONS = [
  SolutionType.Sourcing,
  SolutionType.Investing,
  SolutionType.Management,
  SolutionType.Finance,
]

function TemplateAvailabilityDrawerView<T extends BaseTemplate>({
  template,
  updateEndpoint,
  invalidateQueryKey,
  accessPolicyFeatures = [],
  drawerTitleKey = 'configurations.templateConfig.availability.drawerTitle',
  successMessageKey = 'configurations.templateConfig.availability.saveSuccess',
  errorMessageKey = 'configurations.templateConfig.availability.saveError',
  ...props
}: TemplateAvailabilityDrawerViewProps<T>) {
  const { formatMessage } = useIntl()
  const { mutateAsync, isPending } = useUpdateResource<AvailabilitySaveModel>()
  const { showSnackbar } = useSnackbar()
  const queryClient = useQueryClient()
  const { closeDrawer } = useDrawer()
  const hasAccessPolicy = accessPolicyFeatures.length > 0
  const { policies, isLoading: isLoadingPolicies, isError: isPoliciesError } = useEntitlementPolicies(hasAccessPolicy)
  const { savePolicies, isSaving: isSavingPolicies } = useSaveEntitlementPolicies()
  const [policyEdits, setPolicyEdits] = useState<PolicyEdits>({})

  const [selectedSolutionTypes, setSelectedSolutionTypes] = useState<SolutionType[]>(
    template.solutionAvailability ?? [],
  )
  const [newlySelectedSolutionTypes, setNewlySelectedSolutionTypes] = useState<SolutionType[]>([])

  const [selectedOrganizations, setSelectedOrganizations] = useState<OrganizationAvailability[]>(
    template.organizationAvailability ?? [],
  )
  const [newlySelectedOrganizations, setNewlySelectedOrganizations] = useState<number[]>([])

  const [orgSearchQuery, setOrgSearchQuery] = useState('')
  const [debouncedOrgQuery] = useDebounce(orgSearchQuery, 500)

  const getSolutionTypeLabel = (solutionType: SolutionType) => {
    return formatMessage({ id: `solutions.${solutionType}` })
  }

  const getOrganizationLabel = (org: OrganizationAvailability) => {
    return org.organization?.name ?? `Org #${org.organizationId}`
  }

  // Handlers for solution types
  const addSolutionType = (type: SolutionType) => {
    setSelectedSolutionTypes(prev => [...prev, type])
    setNewlySelectedSolutionTypes(prev => [...prev, type])
  }

  const removeSolutionType = (type: SolutionType) => {
    setSelectedSolutionTypes(prev => prev.filter(t => t !== type))
    setNewlySelectedSolutionTypes(prev => prev.filter(t => t !== type))
  }

  // Handlers for organizations
  const addOrganization = (org: Provider) => {
    setSelectedOrganizations(prev => [
      ...prev,
      {
        id: 0,
        organizationId: org.id,
        organization: { id: org.id, name: org.name },
      },
    ])
    setNewlySelectedOrganizations(prev => [...prev, org.id])
  }

  const removeOrganization = (orgId: number) => {
    setSelectedOrganizations(prev => prev.filter(o => o.organizationId !== orgId))
    setNewlySelectedOrganizations(prev => prev.filter(id => id !== orgId))
  }

  // Get ordered items: existing ones sorted alphabetically, then newly selected ones
  const getOrderedSolutionTypes = () => {
    const existing = selectedSolutionTypes.filter(type => !newlySelectedSolutionTypes.includes(type))
    const sorted = [...existing].sort((a, b) => getSolutionTypeLabel(a).localeCompare(getSolutionTypeLabel(b)))
    return [...sorted, ...newlySelectedSolutionTypes]
  }

  const getOrderedOrganizations = () => {
    const existing = selectedOrganizations.filter(org => !newlySelectedOrganizations.includes(org.organizationId))
    const sorted = [...existing].sort((a, b) => getOrganizationLabel(a).localeCompare(getOrganizationLabel(b)))
    const newlySelected = selectedOrganizations.filter(org => newlySelectedOrganizations.includes(org.organizationId))
    return [...sorted, ...newlySelected]
  }

  const { data: searchedOrganizations = [], isLoading: isLoadingOrgs } = useFetchResource<Provider[]>({
    endpoint: endpoints.findOrganization(debouncedOrgQuery),
    key: [FetchKey.FindOrganization, debouncedOrgQuery],
    options: {
      enabled: debouncedOrgQuery.length > 2,
    },
  })

  const editPolicy = (feature: EntitlementFeature, policy: AccessPolicy) =>
    setPolicyEdits(current => ({ ...current, [feature]: policy }))

  const handleSave = async () => {
    try {
      await mutateAsync({
        url: updateEndpoint,
        body: {
          solutionAvailability: selectedSolutionTypes,
          organizationAvailability: selectedOrganizations.map(org => org.organizationId),
        },
      })
      await savePolicies(changedPolicies(policies, accessPolicyFeatures, template.id, policyEdits))
      showSnackbar({
        message: formatMessage({ id: successMessageKey }, { name: template.name }),
        severity: NotificationSeverity.success,
      })
      queryClient.invalidateQueries({ queryKey: [invalidateQueryKey] })
      closeDrawer()
    } catch {
      showSnackbar({
        message: formatMessage({ id: errorMessageKey }),
        severity: NotificationSeverity.error,
      })
    }
  }

  const orgOptions = searchedOrganizations.filter(
    org => !selectedOrganizations.some(selected => selected.organizationId === org.id),
  )

  return (
    <DrawerView
      title={formatMessage({ id: drawerTitleKey })}
      buttons={[
        {
          label: formatMessage({ id: 'general.save' }),
          variant: 'contained',
          loading: isPending || isSavingPolicies,
          onClick: handleSave,
        },
      ]}
      {...props}
    >
      <Stack spacing={3} px={3} py={2}>
        <Typography variant="subtitle1" fontWeight="bold">
          {template.name}
        </Typography>

        <Divider />

        {/* Solution Type Availability */}
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            {formatMessage({ id: 'configurations.templateConfig.availability.solutionTypeAvailability' })}
          </Typography>

          <Autocomplete
            options={SOLUTION_TYPE_OPTIONS.filter(type => !selectedSolutionTypes.includes(type))}
            onChange={(_event, newValue) => newValue && addSolutionType(newValue)}
            value={null}
            inputValue=""
            getOptionLabel={option => getSolutionTypeLabel(option)}
            renderInput={params => (
              <TextField
                {...params}
                size="small"
                placeholder={formatMessage({ id: 'configurations.templateConfig.availability.selectSolutionTypes' })}
              />
            )}
          />

          {/* Selected solution types */}
          {selectedSolutionTypes.length > 0 && (
            <Stack spacing={0.5} mt={1.5}>
              {getOrderedSolutionTypes().map(type => (
                <Box key={type}>
                  <Chip
                    label={getSolutionTypeLabel(type)}
                    size="small"
                    onDelete={() => removeSolutionType(type)}
                    sx={{ flexDirection: 'row-reverse', '& .MuiChip-deleteIcon': { marginLeft: 0.5, marginRight: 0 } }}
                  />
                </Box>
              ))}
            </Stack>
          )}
        </Box>

        <Divider />

        {/* Organization Availability */}
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            {formatMessage({ id: 'configurations.templateConfig.availability.organizationAvailability' })}
          </Typography>

          <Autocomplete
            options={orgOptions}
            getOptionLabel={option => option.name}
            loading={isLoadingOrgs}
            inputValue={orgSearchQuery}
            onInputChange={(_event, value, reason) => {
              if (reason === 'input') {
                setOrgSearchQuery(value)
              }
            }}
            onChange={(_event, org) => org && addOrganization(org)}
            value={null}
            renderInput={params => (
              <TextField
                {...params}
                size="small"
                placeholder={
                  orgSearchQuery
                    ? undefined
                    : formatMessage({ id: 'configurations.templateConfig.availability.searchOrganizations' })
                }
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText={
              debouncedOrgQuery.length > 2
                ? formatMessage({ id: 'general.noResults' })
                : formatMessage({ id: 'textFieldPlaceholders.searchForEntity' }, { entity: 'organization' })
            }
          />

          {/* Selected organizations (editable) */}
          {selectedOrganizations.length > 0 && (
            <Stack spacing={0.5} mt={1.5}>
              {getOrderedOrganizations().map(orgAvail => (
                <Box key={orgAvail.organizationId}>
                  <Chip
                    label={getOrganizationLabel(orgAvail)}
                    size="small"
                    onDelete={() => removeOrganization(orgAvail.organizationId)}
                    sx={{
                      flexDirection: 'row-reverse',
                      '& .MuiChip-deleteIcon': { marginLeft: 0.5, marginRight: 0 },
                    }}
                  />
                </Box>
              ))}
            </Stack>
          )}
        </Box>

        {hasAccessPolicy && (
          <>
            <Divider />
            <DefaultAccessPolicy
              scopeId={template.id}
              features={accessPolicyFeatures}
              policies={policies}
              isLoading={isLoadingPolicies}
              isError={isPoliciesError}
              edits={policyEdits}
              onChange={editPolicy}
            />
          </>
        )}
      </Stack>
    </DrawerView>
  )
}

export default TemplateAvailabilityDrawerView
